import React, { useState, useEffect, useCallback } from 'react';
import { financeAuditAPI } from '../../services/api';
import Navbar from '../../components/Navbar';
import './FinanceAuditManagement.css';

const FinanceAuditManagement = () => {
  // Active Tab State
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Dashboard State
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardPeriod, setDashboardPeriod] = useState('month');

  // Transactions State
  const [transactions, setTransactions] = useState([]);
  const [transactionsPagination, setTransactionsPagination] = useState({});
  const [transactionFilters, setTransactionFilters] = useState({
    page: 1,
    limit: 20,
    transactionType: '',
    category: '',
    status: '',
    startDate: '',
    endDate: '',
    minAmount: '',
    maxAmount: '',
    hasDiscrepancy: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  const [transactionSummary, setTransactionSummary] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  // Reconciliation State
  const [reconciliations, setReconciliations] = useState([]);
  const [reconciliationsPagination, setReconciliationsPagination] = useState({});
  const [selectedReconciliation, setSelectedReconciliation] = useState(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateForm, setGenerateForm] = useState({
    type: 'DAILY',
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  // Reports State
  const [activeReport, setActiveReport] = useState('revenue');
  const [reportData, setReportData] = useState(null);
  const [reportFilters, setReportFilters] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    groupBy: 'day'
  });

  // Discrepancies State
  const [discrepancies, setDiscrepancies] = useState([]);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [selectedDiscrepancy, setSelectedDiscrepancy] = useState(null);
  const [resolutionText, setResolutionText] = useState('');

  // Adjustment State
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [adjustmentForm, setAdjustmentForm] = useState({
    amount: '',
    description: '',
    internalNotes: '',
    orderId: '',
    restaurantId: '',
    deliveryPartnerId: '',
    customerId: ''
  });

  // ============================================================
  // API CALLS
  // ============================================================

  // Dashboard
  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const response = await financeAuditAPI.getDashboard({ period: dashboardPeriod });
      setDashboardData(response.data.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      setMessage({ type: 'error', text: 'Failed to load dashboard data' });
    } finally {
      setLoading(false);
    }
  }, [dashboardPeriod]);

  // Transactions
  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...transactionFilters };
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) delete params[key];
      });
      const response = await financeAuditAPI.getTransactions(params);
      setTransactions(response.data.data.transactions);
      setTransactionsPagination(response.data.data.pagination);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
      setMessage({ type: 'error', text: 'Failed to load transactions' });
    } finally {
      setLoading(false);
    }
  }, [transactionFilters]);

  const fetchTransactionSummary = async () => {
    try {
      const response = await financeAuditAPI.getTransactionSummary({
        startDate: transactionFilters.startDate,
        endDate: transactionFilters.endDate
      });
      setTransactionSummary(response.data.data);
    } catch (error) {
      console.error('Failed to fetch transaction summary:', error);
    }
  };

  const fetchTransactionDetails = async (auditId) => {
    try {
      const response = await financeAuditAPI.getTransaction(auditId);
      setSelectedTransaction(response.data.data);
    } catch (error) {
      console.error('Failed to fetch transaction details:', error);
      setMessage({ type: 'error', text: 'Failed to load transaction details' });
    }
  };

  // Reconciliation
  const fetchReconciliations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await financeAuditAPI.getReconciliations({ limit: 20 });
      setReconciliations(response.data.data.reconciliations);
      setReconciliationsPagination(response.data.data.pagination);
    } catch (error) {
      console.error('Failed to fetch reconciliations:', error);
      setMessage({ type: 'error', text: 'Failed to load reconciliation reports' });
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchReconciliationDetails = async (reconciliationId) => {
    try {
      const response = await financeAuditAPI.getReconciliation(reconciliationId);
      setSelectedReconciliation(response.data.data);
    } catch (error) {
      console.error('Failed to fetch reconciliation details:', error);
      setMessage({ type: 'error', text: 'Failed to load reconciliation details' });
    }
  };

  const generateReconciliation = async () => {
    setLoading(true);
    try {
      await financeAuditAPI.generateReconciliation(generateForm);
      setMessage({ type: 'success', text: 'Reconciliation report generated successfully!' });
      setShowGenerateModal(false);
      fetchReconciliations();
    } catch (error) {
      console.error('Failed to generate reconciliation:', error);
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to generate reconciliation' });
    } finally {
      setLoading(false);
    }
  };

  const approveReconciliation = async (reconciliationId, notes) => {
    setLoading(true);
    try {
      await financeAuditAPI.approveReconciliation(reconciliationId, { notes });
      setMessage({ type: 'success', text: 'Reconciliation approved successfully!' });
      fetchReconciliations();
      if (selectedReconciliation?._id === reconciliationId) {
        fetchReconciliationDetails(reconciliationId);
      }
    } catch (error) {
      console.error('Failed to approve reconciliation:', error);
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to approve reconciliation' });
    } finally {
      setLoading(false);
    }
  };

  // Reports
  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      let response;
      const params = { ...reportFilters };

      switch (activeReport) {
        case 'revenue':
          response = await financeAuditAPI.getRevenueReport(params);
          break;
        case 'commissions':
          response = await financeAuditAPI.getCommissionReport(params);
          break;
        case 'tds':
          response = await financeAuditAPI.getTdsReport(params);
          break;
        case 'gst':
          response = await financeAuditAPI.getGstReport(params);
          break;
        case 'subscriptions':
          response = await financeAuditAPI.getSubscriptionReport(params);
          break;
        default:
          return;
      }
      setReportData(response.data.data);
    } catch (error) {
      console.error('Failed to fetch report:', error);
      setMessage({ type: 'error', text: 'Failed to load report' });
    } finally {
      setLoading(false);
    }
  }, [activeReport, reportFilters]);

  // Discrepancies
  const fetchDiscrepancies = useCallback(async () => {
    setLoading(true);
    try {
      const response = await financeAuditAPI.getDiscrepancies({});
      setDiscrepancies(response.data.data.discrepancies || []);
    } catch (error) {
      console.error('Failed to fetch discrepancies:', error);
      setMessage({ type: 'error', text: 'Failed to load discrepancies' });
    } finally {
      setLoading(false);
    }
  }, []);

  const resolveDiscrepancy = async () => {
    if (!selectedDiscrepancy || !resolutionText) return;

    setLoading(true);
    try {
      await financeAuditAPI.resolveDiscrepancy(
        selectedDiscrepancy.reconciliationId,
        selectedDiscrepancy.index,
        { resolution: resolutionText }
      );
      setMessage({ type: 'success', text: 'Discrepancy resolved successfully!' });
      setShowResolveModal(false);
      setResolutionText('');
      setSelectedDiscrepancy(null);
      fetchDiscrepancies();
    } catch (error) {
      console.error('Failed to resolve discrepancy:', error);
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to resolve discrepancy' });
    } finally {
      setLoading(false);
    }
  };

  // Adjustments
  const createAdjustment = async () => {
    setLoading(true);
    try {
      const data = { ...adjustmentForm };
      Object.keys(data).forEach(key => {
        if (data[key] === '') delete data[key];
      });
      data.amount = parseFloat(data.amount);

      await financeAuditAPI.createAdjustment(data);
      setMessage({ type: 'success', text: 'Adjustment created successfully!' });
      setShowAdjustmentModal(false);
      setAdjustmentForm({
        amount: '',
        description: '',
        internalNotes: '',
        orderId: '',
        restaurantId: '',
        deliveryPartnerId: '',
        customerId: ''
      });
      fetchTransactions();
    } catch (error) {
      console.error('Failed to create adjustment:', error);
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to create adjustment' });
    } finally {
      setLoading(false);
    }
  };

  // Export
  const exportData = async (format, type) => {
    try {
      const response = await financeAuditAPI.exportAuditData({
        format,
        type,
        startDate: transactionFilters.startDate,
        endDate: transactionFilters.endDate
      });

      if (format === 'csv') {
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `audit-${type}-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
      } else {
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `audit-${type}-${new Date().toISOString().split('T')[0]}.json`;
        a.click();
      }

      setMessage({ type: 'success', text: 'Export downloaded successfully!' });
    } catch (error) {
      console.error('Failed to export data:', error);
      setMessage({ type: 'error', text: 'Failed to export data' });
    }
  };

  // ============================================================
  // EFFECTS
  // ============================================================

  useEffect(() => {
    if (activeTab === 'dashboard') {
      fetchDashboard();
    } else if (activeTab === 'transactions') {
      fetchTransactions();
      fetchTransactionSummary();
    } else if (activeTab === 'reconciliation') {
      fetchReconciliations();
    } else if (activeTab === 'reports') {
      fetchReport();
    } else if (activeTab === 'discrepancies') {
      fetchDiscrepancies();
    }
  }, [activeTab, fetchDashboard, fetchTransactions, fetchReconciliations, fetchReport, fetchDiscrepancies]);

  useEffect(() => {
    if (activeTab === 'dashboard') {
      fetchDashboard();
    }
  }, [dashboardPeriod, fetchDashboard]);

  useEffect(() => {
    if (activeTab === 'reports') {
      fetchReport();
    }
  }, [activeReport, reportFilters, fetchReport]);

  // Clear message after 5 seconds
  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // ============================================================
  // HELPER FUNCTIONS
  // ============================================================

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTransactionTypeColor = (type) => {
    const colors = {
      ORDER_PAYMENT: '#10b981',
      ORDER_REFUND: '#ef4444',
      PLATFORM_FEE: '#8b5cf6',
      DELIVERY_COMMISSION: '#f59e0b',
      GST_COLLECTION: '#3b82f6',
      TDS_DEDUCTION: '#6366f1',
      RESTAURANT_PAYOUT: '#ec4899',
      DELIVERY_PAYOUT: '#14b8a6',
      SUBSCRIPTION_CHARGE: '#f97316',
      COD_SETTLEMENT: '#84cc16',
      SURGE_COLLECTION: '#a855f7',
      ADDON_REVENUE: '#06b6d4',
      ADJUSTMENT: '#64748b'
    };
    return colors[type] || '#6b7280';
  };

  const getTransactionTypeIcon = (type) => {
    const icons = {
      ORDER_PAYMENT: '💳',
      ORDER_REFUND: '↩️',
      PLATFORM_FEE: '🏢',
      DELIVERY_COMMISSION: '🚴',
      GST_COLLECTION: '📋',
      TDS_DEDUCTION: '📑',
      RESTAURANT_PAYOUT: '🏪',
      DELIVERY_PAYOUT: '💰',
      SUBSCRIPTION_CHARGE: '📅',
      COD_SETTLEMENT: '💵',
      SURGE_COLLECTION: '⚡',
      ADDON_REVENUE: '➕',
      ADJUSTMENT: '🔧'
    };
    return icons[type] || '💹';
  };

  const getCategoryBadge = (category) => {
    const styles = {
      INFLOW: { bg: '#dcfce7', color: '#166534', label: '↓ Inflow' },
      OUTFLOW: { bg: '#fee2e2', color: '#991b1b', label: '↑ Outflow' },
      COMMISSION: { bg: '#fef3c7', color: '#92400e', label: '% Commission' },
      TAX: { bg: '#dbeafe', color: '#1e40af', label: '📋 Tax' },
      ADJUSTMENT: { bg: '#f3f4f6', color: '#374151', label: '🔧 Adjustment' }
    };
    const style = styles[category] || styles.ADJUSTMENT;
    return (
      <span className="category-badge" style={{ backgroundColor: style.bg, color: style.color }}>
        {style.label}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const styles = {
      COMPLETED: { bg: '#dcfce7', color: '#166534' },
      PENDING: { bg: '#fef3c7', color: '#92400e' },
      PROCESSING: { bg: '#dbeafe', color: '#1e40af' },
      FAILED: { bg: '#fee2e2', color: '#991b1b' },
      REVERSED: { bg: '#f3e8ff', color: '#6b21a8' },
      APPROVED: { bg: '#dcfce7', color: '#166534' },
      PENDING_APPROVAL: { bg: '#fef3c7', color: '#92400e' }
    };
    const style = styles[status] || { bg: '#f3f4f6', color: '#374151' };
    return (
      <span className="status-badge" style={{ backgroundColor: style.bg, color: style.color }}>
        {status}
      </span>
    );
  };

  // ============================================================
  // RENDER FUNCTIONS
  // ============================================================

  // Dashboard Tab
  const renderDashboard = () => (
    <div className="audit-dashboard">
      {/* Period Selector */}
      <div className="period-selector">
        <label>Period:</label>
        <select value={dashboardPeriod} onChange={(e) => setDashboardPeriod(e.target.value)}>
          <option value="day">Today</option>
          <option value="week">This Week</option>
          <option value="month">This Month</option>
          <option value="quarter">This Quarter</option>
          <option value="year">This Year</option>
        </select>
      </div>

      {/* Summary Cards */}
      <div className="dashboard-grid">
        <div className="summary-card inflow">
          <div className="card-icon">💰</div>
          <div className="card-content">
            <h3>Total Inflow</h3>
            <p className="amount">{formatCurrency(dashboardData?.summary?.totalInflow)}</p>
            <span className="sub-text">{dashboardData?.summary?.inflowCount || 0} transactions</span>
          </div>
        </div>

        <div className="summary-card outflow">
          <div className="card-icon">📤</div>
          <div className="card-content">
            <h3>Total Outflow</h3>
            <p className="amount">{formatCurrency(dashboardData?.summary?.totalOutflow)}</p>
            <span className="sub-text">{dashboardData?.summary?.outflowCount || 0} transactions</span>
          </div>
        </div>

        <div className="summary-card commission">
          <div className="card-icon">📊</div>
          <div className="card-content">
            <h3>Total Commission</h3>
            <p className="amount">{formatCurrency(dashboardData?.summary?.totalCommission)}</p>
            <span className="sub-text">Platform + Delivery</span>
          </div>
        </div>

        <div className="summary-card tax">
          <div className="card-icon">📋</div>
          <div className="card-content">
            <h3>Total Tax</h3>
            <p className="amount">{formatCurrency(dashboardData?.summary?.totalTax)}</p>
            <span className="sub-text">GST + TDS collected</span>
          </div>
        </div>

        <div className="summary-card net">
          <div className="card-icon">💹</div>
          <div className="card-content">
            <h3>Net Position</h3>
            <p className="amount" style={{ color: (dashboardData?.summary?.netPosition || 0) >= 0 ? '#10b981' : '#ef4444' }}>
              {formatCurrency(dashboardData?.summary?.netPosition)}
            </p>
            <span className="sub-text">Inflow - Outflow</span>
          </div>
        </div>

        <div className="summary-card transactions">
          <div className="card-icon">📝</div>
          <div className="card-content">
            <h3>Total Transactions</h3>
            <p className="amount">{dashboardData?.summary?.totalTransactions || 0}</p>
            <span className="sub-text">All categories</span>
          </div>
        </div>
      </div>

      {/* Breakdown by Type */}
      <div className="breakdown-section">
        <h3>📊 Breakdown by Transaction Type</h3>
        <div className="breakdown-grid">
          {dashboardData?.byType?.map((item, index) => (
            <div key={index} className="breakdown-item" style={{ borderLeftColor: getTransactionTypeColor(item._id) }}>
              <div className="breakdown-header">
                <span className="breakdown-icon">{getTransactionTypeIcon(item._id)}</span>
                <span className="breakdown-type">{item._id?.replace(/_/g, ' ')}</span>
              </div>
              <div className="breakdown-stats">
                <div className="breakdown-amount">{formatCurrency(item.totalAmount)}</div>
                <div className="breakdown-count">{item.count} txns</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Reconciliations */}
      {dashboardData?.recentReconciliations?.length > 0 && (
        <div className="recent-section">
          <h3>📑 Recent Reconciliations</h3>
          <div className="recent-list">
            {dashboardData.recentReconciliations.map((rec, index) => (
              <div key={index} className="recent-item">
                <div className="recent-info">
                  <span className="recent-type">{rec.type}</span>
                  <span className="recent-period">{rec.period?.display}</span>
                </div>
                <div className="recent-stats">
                  <span className="recent-score" style={{
                    color: rec.summary?.reconciliationScore >= 90 ? '#10b981' :
                           rec.summary?.reconciliationScore >= 70 ? '#f59e0b' : '#ef4444'
                  }}>
                    Score: {rec.summary?.reconciliationScore?.toFixed(1)}%
                  </span>
                  {getStatusBadge(rec.status)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Discrepancy Alerts */}
      {dashboardData?.discrepancyAlerts?.length > 0 && (
        <div className="alert-section">
          <h3>⚠️ Discrepancy Alerts</h3>
          <div className="alert-list">
            {dashboardData.discrepancyAlerts.map((alert, index) => (
              <div key={index} className="alert-item" style={{
                borderLeftColor: alert.severity === 'HIGH' ? '#ef4444' :
                                 alert.severity === 'MEDIUM' ? '#f59e0b' : '#3b82f6'
              }}>
                <div className="alert-info">
                  <span className="alert-category">{alert.category}</span>
                  <span className="alert-description">{alert.description}</span>
                </div>
                <div className="alert-amount">
                  {formatCurrency(alert.amount)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // Transactions Tab
  const renderTransactions = () => (
    <div className="audit-transactions">
      {/* Filters */}
      <div className="filters-section">
        <h3>🔍 Filters</h3>
        <div className="filters-grid">
          <div className="filter-group">
            <label>Transaction Type</label>
            <select
              value={transactionFilters.transactionType}
              onChange={(e) => setTransactionFilters({...transactionFilters, transactionType: e.target.value, page: 1})}
            >
              <option value="">All Types</option>
              <option value="ORDER_PAYMENT">Order Payment</option>
              <option value="ORDER_REFUND">Order Refund</option>
              <option value="PLATFORM_FEE">Platform Fee</option>
              <option value="DELIVERY_COMMISSION">Delivery Commission</option>
              <option value="GST_COLLECTION">GST Collection</option>
              <option value="TDS_DEDUCTION">TDS Deduction</option>
              <option value="RESTAURANT_PAYOUT">Restaurant Payout</option>
              <option value="DELIVERY_PAYOUT">Delivery Payout</option>
              <option value="SUBSCRIPTION_CHARGE">Subscription</option>
              <option value="COD_SETTLEMENT">COD Settlement</option>
              <option value="SURGE_COLLECTION">Surge Collection</option>
              <option value="ADJUSTMENT">Adjustment</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Category</label>
            <select
              value={transactionFilters.category}
              onChange={(e) => setTransactionFilters({...transactionFilters, category: e.target.value, page: 1})}
            >
              <option value="">All Categories</option>
              <option value="INFLOW">Inflow</option>
              <option value="OUTFLOW">Outflow</option>
              <option value="COMMISSION">Commission</option>
              <option value="TAX">Tax</option>
              <option value="ADJUSTMENT">Adjustment</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Status</label>
            <select
              value={transactionFilters.status}
              onChange={(e) => setTransactionFilters({...transactionFilters, status: e.target.value, page: 1})}
            >
              <option value="">All Status</option>
              <option value="COMPLETED">Completed</option>
              <option value="PENDING">Pending</option>
              <option value="PROCESSING">Processing</option>
              <option value="FAILED">Failed</option>
              <option value="REVERSED">Reversed</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Discrepancy</label>
            <select
              value={transactionFilters.hasDiscrepancy}
              onChange={(e) => setTransactionFilters({...transactionFilters, hasDiscrepancy: e.target.value, page: 1})}
            >
              <option value="">All</option>
              <option value="true">Has Discrepancy</option>
              <option value="false">No Discrepancy</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Start Date</label>
            <input
              type="date"
              value={transactionFilters.startDate}
              onChange={(e) => setTransactionFilters({...transactionFilters, startDate: e.target.value, page: 1})}
            />
          </div>

          <div className="filter-group">
            <label>End Date</label>
            <input
              type="date"
              value={transactionFilters.endDate}
              onChange={(e) => setTransactionFilters({...transactionFilters, endDate: e.target.value, page: 1})}
            />
          </div>

          <div className="filter-group">
            <label>Min Amount</label>
            <input
              type="number"
              placeholder="Min"
              value={transactionFilters.minAmount}
              onChange={(e) => setTransactionFilters({...transactionFilters, minAmount: e.target.value, page: 1})}
            />
          </div>

          <div className="filter-group">
            <label>Max Amount</label>
            <input
              type="number"
              placeholder="Max"
              value={transactionFilters.maxAmount}
              onChange={(e) => setTransactionFilters({...transactionFilters, maxAmount: e.target.value, page: 1})}
            />
          </div>
        </div>

        <div className="filter-actions">
          <button className="btn-secondary" onClick={() => {
            setTransactionFilters({
              page: 1, limit: 20, transactionType: '', category: '', status: '',
              startDate: '', endDate: '', minAmount: '', maxAmount: '',
              hasDiscrepancy: '', sortBy: 'createdAt', sortOrder: 'desc'
            });
          }}>
            Clear Filters
          </button>
          <button className="btn-primary" onClick={fetchTransactions}>
            Apply Filters
          </button>
          <button className="btn-export" onClick={() => exportData('csv', 'transactions')}>
            📥 Export CSV
          </button>
          <button className="btn-adjustment" onClick={() => setShowAdjustmentModal(true)}>
            ➕ Create Adjustment
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {transactionSummary && (
        <div className="transaction-summary">
          <h3>📊 Summary</h3>
          <div className="summary-mini-grid">
            {transactionSummary.map((item, index) => (
              <div key={index} className="summary-mini-card">
                <span className="mini-label">{item._id?.replace(/_/g, ' ')}</span>
                <span className="mini-amount">{formatCurrency(item.totalAmount)}</span>
                <span className="mini-count">{item.count} txns</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Transactions Table */}
      <div className="transactions-table-container">
        <table className="transactions-table">
          <thead>
            <tr>
              <th>Audit ID</th>
              <th>Type</th>
              <th>Category</th>
              <th>Amount</th>
              <th>Direction</th>
              <th>Status</th>
              <th>Order #</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {transactions.map((txn) => (
              <tr key={txn._id} className={txn.discrepancy?.hasDiscrepancy ? 'has-discrepancy' : ''}>
                <td className="audit-id">
                  <code>{txn.auditId}</code>
                </td>
                <td>
                  <span className="type-badge" style={{ backgroundColor: `${getTransactionTypeColor(txn.transactionType)}20`, color: getTransactionTypeColor(txn.transactionType) }}>
                    {getTransactionTypeIcon(txn.transactionType)} {txn.transactionType?.replace(/_/g, ' ')}
                  </span>
                </td>
                <td>{getCategoryBadge(txn.category)}</td>
                <td className={`amount ${txn.direction === 'CREDIT' ? 'credit' : 'debit'}`}>
                  {txn.direction === 'CREDIT' ? '+' : '-'}{formatCurrency(txn.amount)}
                </td>
                <td>
                  <span className={`direction-badge ${txn.direction?.toLowerCase()}`}>
                    {txn.direction === 'CREDIT' ? '↓' : '↑'} {txn.direction}
                  </span>
                </td>
                <td>{getStatusBadge(txn.status)}</td>
                <td>{txn.orderId?.orderNumber || '-'}</td>
                <td>{formatDate(txn.createdAt)}</td>
                <td>
                  <button className="btn-view" onClick={() => fetchTransactionDetails(txn.auditId)}>
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {transactions.length === 0 && !loading && (
          <div className="no-data">No transactions found</div>
        )}
      </div>

      {/* Pagination */}
      {transactionsPagination.totalPages > 1 && (
        <div className="pagination">
          <button
            disabled={transactionFilters.page <= 1}
            onClick={() => setTransactionFilters({...transactionFilters, page: transactionFilters.page - 1})}
          >
            Previous
          </button>
          <span>Page {transactionFilters.page} of {transactionsPagination.totalPages}</span>
          <button
            disabled={transactionFilters.page >= transactionsPagination.totalPages}
            onClick={() => setTransactionFilters({...transactionFilters, page: transactionFilters.page + 1})}
          >
            Next
          </button>
        </div>
      )}

      {/* Transaction Detail Modal */}
      {selectedTransaction && (
        <div className="modal-overlay" onClick={() => setSelectedTransaction(null)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📋 Transaction Details</h3>
              <button className="modal-close" onClick={() => setSelectedTransaction(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Audit ID</label>
                  <code>{selectedTransaction.auditId}</code>
                </div>
                <div className="detail-item">
                  <label>Type</label>
                  <span className="type-badge" style={{ backgroundColor: `${getTransactionTypeColor(selectedTransaction.transactionType)}20`, color: getTransactionTypeColor(selectedTransaction.transactionType) }}>
                    {getTransactionTypeIcon(selectedTransaction.transactionType)} {selectedTransaction.transactionType}
                  </span>
                </div>
                <div className="detail-item">
                  <label>Amount</label>
                  <span className={`amount ${selectedTransaction.direction === 'CREDIT' ? 'credit' : 'debit'}`}>
                    {formatCurrency(selectedTransaction.amount)}
                  </span>
                </div>
                <div className="detail-item">
                  <label>Status</label>
                  {getStatusBadge(selectedTransaction.status)}
                </div>
                <div className="detail-item">
                  <label>Financial Year</label>
                  <span>{selectedTransaction.financialYear}</span>
                </div>
                <div className="detail-item">
                  <label>Created</label>
                  <span>{formatDate(selectedTransaction.createdAt)}</span>
                </div>
              </div>

              {selectedTransaction.breakdown && (
                <div className="breakdown-detail">
                  <h4>💰 Amount Breakdown</h4>
                  <div className="breakdown-list">
                    {Object.entries(selectedTransaction.breakdown).map(([key, value]) => (
                      value !== undefined && value !== null && (
                        <div key={key} className="breakdown-row">
                          <span>{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                          <span>{typeof value === 'number' ? formatCurrency(value) : value}</span>
                        </div>
                      )
                    ))}
                  </div>
                </div>
              )}

              {selectedTransaction.rateSnapshot && (
                <div className="rate-snapshot">
                  <h4>📊 Rate Snapshot (at transaction time)</h4>
                  <div className="rate-list">
                    {Object.entries(selectedTransaction.rateSnapshot).map(([key, value]) => (
                      value !== undefined && value !== null && (
                        <div key={key} className="rate-row">
                          <span>{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                          <span>{typeof value === 'number' ? `${value}%` : value}</span>
                        </div>
                      )
                    ))}
                  </div>
                </div>
              )}

              {selectedTransaction.discrepancy?.hasDiscrepancy && (
                <div className="discrepancy-alert">
                  <h4>⚠️ Discrepancy Detected</h4>
                  <p>{selectedTransaction.discrepancy.description}</p>
                  <div className="discrepancy-amounts">
                    <span>Expected: {formatCurrency(selectedTransaction.discrepancy.expectedAmount)}</span>
                    <span>Actual: {formatCurrency(selectedTransaction.discrepancy.actualAmount)}</span>
                    <span>Difference: {formatCurrency(selectedTransaction.discrepancy.difference)}</span>
                  </div>
                </div>
              )}

              {selectedTransaction.metadata && Object.keys(selectedTransaction.metadata).length > 0 && (
                <div className="metadata-section">
                  <h4>📎 Metadata</h4>
                  <pre>{JSON.stringify(selectedTransaction.metadata, null, 2)}</pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Reconciliation Tab
  const renderReconciliation = () => (
    <div className="audit-reconciliation">
      {/* Actions */}
      <div className="reconciliation-actions">
        <button className="btn-primary" onClick={() => setShowGenerateModal(true)}>
          ➕ Generate New Reconciliation
        </button>
        <button className="btn-secondary" onClick={fetchReconciliations}>
          🔄 Refresh
        </button>
      </div>

      {/* Reconciliation List */}
      <div className="reconciliation-list">
        {reconciliations.map((rec) => (
          <div key={rec._id} className="reconciliation-card" onClick={() => fetchReconciliationDetails(rec._id)}>
            <div className="rec-header">
              <div className="rec-type-badge">{rec.type}</div>
              {getStatusBadge(rec.status)}
            </div>
            <div className="rec-period">
              <span className="period-label">Period:</span>
              <span className="period-value">{rec.period?.display}</span>
            </div>
            <div className="rec-stats">
              <div className="rec-stat">
                <span className="stat-label">Orders</span>
                <span className="stat-value">{rec.orderTransactions?.totalOrders || 0}</span>
              </div>
              <div className="rec-stat">
                <span className="stat-label">Revenue</span>
                <span className="stat-value">{formatCurrency(rec.platformRevenue?.totalPlatformFees)}</span>
              </div>
              <div className="rec-stat">
                <span className="stat-label">Discrepancies</span>
                <span className="stat-value" style={{ color: rec.discrepancies?.length > 0 ? '#ef4444' : '#10b981' }}>
                  {rec.discrepancies?.length || 0}
                </span>
              </div>
              <div className="rec-stat">
                <span className="stat-label">Score</span>
                <span className="stat-value" style={{
                  color: rec.summary?.reconciliationScore >= 90 ? '#10b981' :
                         rec.summary?.reconciliationScore >= 70 ? '#f59e0b' : '#ef4444'
                }}>
                  {rec.summary?.reconciliationScore?.toFixed(1)}%
                </span>
              </div>
            </div>
            <div className="rec-footer">
              <span className="rec-date">Generated: {formatDate(rec.createdAt)}</span>
              {rec.approvedBy && (
                <span className="rec-approved">✅ Approved by {rec.approvedBy?.name}</span>
              )}
            </div>
          </div>
        ))}

        {reconciliations.length === 0 && !loading && (
          <div className="no-data">No reconciliation reports found. Generate one to get started!</div>
        )}
      </div>

      {/* Generate Modal */}
      {showGenerateModal && (
        <div className="modal-overlay" onClick={() => setShowGenerateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📊 Generate Reconciliation Report</h3>
              <button className="modal-close" onClick={() => setShowGenerateModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Report Type</label>
                <select
                  value={generateForm.type}
                  onChange={(e) => setGenerateForm({...generateForm, type: e.target.value})}
                >
                  <option value="DAILY">Daily</option>
                  <option value="WEEKLY">Weekly</option>
                  <option value="MONTHLY">Monthly</option>
                  <option value="CUSTOM">Custom Range</option>
                </select>
              </div>
              <div className="form-group">
                <label>Start Date</label>
                <input
                  type="date"
                  value={generateForm.startDate}
                  onChange={(e) => setGenerateForm({...generateForm, startDate: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>End Date</label>
                <input
                  type="date"
                  value={generateForm.endDate}
                  onChange={(e) => setGenerateForm({...generateForm, endDate: e.target.value})}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowGenerateModal(false)}>Cancel</button>
              <button className="btn-primary" onClick={generateReconciliation} disabled={loading}>
                {loading ? 'Generating...' : 'Generate Report'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reconciliation Detail Modal */}
      {selectedReconciliation && (
        <div className="modal-overlay" onClick={() => setSelectedReconciliation(null)}>
          <div className="modal-content extra-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📑 Reconciliation Report: {selectedReconciliation.type}</h3>
              <button className="modal-close" onClick={() => setSelectedReconciliation(null)}>×</button>
            </div>
            <div className="modal-body">
              {/* Summary */}
              <div className="rec-detail-section">
                <h4>📊 Summary</h4>
                <div className="rec-detail-grid">
                  <div className="rec-detail-item">
                    <label>Period</label>
                    <span>{selectedReconciliation.period?.display}</span>
                  </div>
                  <div className="rec-detail-item">
                    <label>Status</label>
                    {getStatusBadge(selectedReconciliation.status)}
                  </div>
                  <div className="rec-detail-item">
                    <label>Reconciliation Score</label>
                    <span className="score" style={{
                      color: selectedReconciliation.summary?.reconciliationScore >= 90 ? '#10b981' :
                             selectedReconciliation.summary?.reconciliationScore >= 70 ? '#f59e0b' : '#ef4444'
                    }}>
                      {selectedReconciliation.summary?.reconciliationScore?.toFixed(1)}%
                    </span>
                  </div>
                  <div className="rec-detail-item">
                    <label>Total Discrepancies</label>
                    <span style={{ color: selectedReconciliation.summary?.totalDiscrepancies > 0 ? '#ef4444' : '#10b981' }}>
                      {selectedReconciliation.summary?.totalDiscrepancies || 0}
                    </span>
                  </div>
                </div>
              </div>

              {/* Order Transactions */}
              <div className="rec-detail-section">
                <h4>📦 Order Transactions</h4>
                <div className="rec-detail-grid">
                  <div className="rec-detail-item">
                    <label>Total Orders</label>
                    <span>{selectedReconciliation.orderTransactions?.totalOrders || 0}</span>
                  </div>
                  <div className="rec-detail-item">
                    <label>Total GMV</label>
                    <span>{formatCurrency(selectedReconciliation.orderTransactions?.totalGMV)}</span>
                  </div>
                  <div className="rec-detail-item">
                    <label>Completed</label>
                    <span>{selectedReconciliation.orderTransactions?.completedOrders || 0}</span>
                  </div>
                  <div className="rec-detail-item">
                    <label>Cancelled</label>
                    <span>{selectedReconciliation.orderTransactions?.cancelledOrders || 0}</span>
                  </div>
                </div>
              </div>

              {/* Platform Revenue */}
              <div className="rec-detail-section">
                <h4>🏢 Platform Revenue</h4>
                <div className="rec-detail-grid">
                  <div className="rec-detail-item">
                    <label>Platform Fees</label>
                    <span>{formatCurrency(selectedReconciliation.platformRevenue?.totalPlatformFees)}</span>
                  </div>
                  <div className="rec-detail-item">
                    <label>Delivery Commission</label>
                    <span>{formatCurrency(selectedReconciliation.platformRevenue?.totalDeliveryCommission)}</span>
                  </div>
                  <div className="rec-detail-item">
                    <label>Surge Revenue</label>
                    <span>{formatCurrency(selectedReconciliation.platformRevenue?.totalSurgeRevenue)}</span>
                  </div>
                  <div className="rec-detail-item highlight">
                    <label>Net Platform Revenue</label>
                    <span>{formatCurrency(selectedReconciliation.platformRevenue?.netPlatformRevenue)}</span>
                  </div>
                </div>
              </div>

              {/* Tax Compliance */}
              <div className="rec-detail-section">
                <h4>📋 Tax Compliance</h4>
                <div className="rec-detail-grid">
                  <div className="rec-detail-item">
                    <label>GST Collected</label>
                    <span>{formatCurrency(selectedReconciliation.taxCompliance?.gstCollected?.total)}</span>
                  </div>
                  <div className="rec-detail-item">
                    <label>TDS Deducted</label>
                    <span>{formatCurrency(selectedReconciliation.taxCompliance?.tdsDeducted?.total)}</span>
                  </div>
                </div>
              </div>

              {/* Discrepancies */}
              {selectedReconciliation.discrepancies?.length > 0 && (
                <div className="rec-detail-section discrepancies">
                  <h4>⚠️ Discrepancies ({selectedReconciliation.discrepancies.length})</h4>
                  <div className="discrepancy-list">
                    {selectedReconciliation.discrepancies.map((disc, index) => (
                      <div key={index} className="discrepancy-item" style={{
                        borderLeftColor: disc.severity === 'HIGH' ? '#ef4444' :
                                        disc.severity === 'MEDIUM' ? '#f59e0b' : '#3b82f6'
                      }}>
                        <div className="disc-header">
                          <span className="disc-category">{disc.category}</span>
                          <span className={`disc-severity ${disc.severity?.toLowerCase()}`}>{disc.severity}</span>
                        </div>
                        <p className="disc-description">{disc.description}</p>
                        <div className="disc-amounts">
                          <span>Expected: {formatCurrency(disc.expectedAmount)}</span>
                          <span>Actual: {formatCurrency(disc.actualAmount)}</span>
                          <span className="disc-diff">Diff: {formatCurrency(disc.amount)}</span>
                        </div>
                        {disc.status === 'PENDING' && (
                          <button
                            className="btn-resolve"
                            onClick={() => {
                              setSelectedDiscrepancy({ ...disc, reconciliationId: selectedReconciliation._id, index });
                              setShowResolveModal(true);
                            }}
                          >
                            Resolve
                          </button>
                        )}
                        {disc.status === 'RESOLVED' && (
                          <div className="disc-resolved">
                            ✅ Resolved: {disc.resolution}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Approval Section */}
              {selectedReconciliation.status === 'PENDING_APPROVAL' && (
                <div className="approval-section">
                  <button
                    className="btn-approve"
                    onClick={() => {
                      const notes = prompt('Enter approval notes (optional):');
                      if (notes !== null) {
                        approveReconciliation(selectedReconciliation._id, notes);
                      }
                    }}
                  >
                    ✅ Approve Reconciliation
                  </button>
                </div>
              )}

              {selectedReconciliation.approvedBy && (
                <div className="approved-info">
                  <span>✅ Approved by {selectedReconciliation.approvedBy?.name} on {formatDate(selectedReconciliation.approvedAt)}</span>
                  {selectedReconciliation.approvalNotes && (
                    <p>Notes: {selectedReconciliation.approvalNotes}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Reports Tab
  const renderReports = () => (
    <div className="audit-reports">
      {/* Report Type Selector */}
      <div className="report-tabs">
        <button
          className={`report-tab ${activeReport === 'revenue' ? 'active' : ''}`}
          onClick={() => setActiveReport('revenue')}
        >
          💰 Revenue
        </button>
        <button
          className={`report-tab ${activeReport === 'commissions' ? 'active' : ''}`}
          onClick={() => setActiveReport('commissions')}
        >
          📊 Commissions
        </button>
        <button
          className={`report-tab ${activeReport === 'tds' ? 'active' : ''}`}
          onClick={() => setActiveReport('tds')}
        >
          📑 TDS
        </button>
        <button
          className={`report-tab ${activeReport === 'gst' ? 'active' : ''}`}
          onClick={() => setActiveReport('gst')}
        >
          📋 GST
        </button>
        <button
          className={`report-tab ${activeReport === 'subscriptions' ? 'active' : ''}`}
          onClick={() => setActiveReport('subscriptions')}
        >
          📅 Subscriptions
        </button>
      </div>

      {/* Report Filters */}
      <div className="report-filters">
        <div className="filter-group">
          <label>Start Date</label>
          <input
            type="date"
            value={reportFilters.startDate}
            onChange={(e) => setReportFilters({...reportFilters, startDate: e.target.value})}
          />
        </div>
        <div className="filter-group">
          <label>End Date</label>
          <input
            type="date"
            value={reportFilters.endDate}
            onChange={(e) => setReportFilters({...reportFilters, endDate: e.target.value})}
          />
        </div>
        {activeReport === 'revenue' && (
          <div className="filter-group">
            <label>Group By</label>
            <select
              value={reportFilters.groupBy}
              onChange={(e) => setReportFilters({...reportFilters, groupBy: e.target.value})}
            >
              <option value="hour">Hour</option>
              <option value="day">Day</option>
              <option value="week">Week</option>
              <option value="month">Month</option>
            </select>
          </div>
        )}
        <button className="btn-primary" onClick={fetchReport}>
          Generate Report
        </button>
      </div>

      {/* Report Content */}
      <div className="report-content">
        {loading ? (
          <div className="loading">Loading report...</div>
        ) : reportData ? (
          <>
            {/* Revenue Report */}
            {activeReport === 'revenue' && (
              <div className="revenue-report">
                <div className="report-summary">
                  <h3>💰 Revenue Summary</h3>
                  <div className="summary-grid">
                    <div className="summary-item">
                      <label>Total Platform Fees</label>
                      <span>{formatCurrency(reportData.totals?.platformFees)}</span>
                    </div>
                    <div className="summary-item">
                      <label>Total Delivery Commission</label>
                      <span>{formatCurrency(reportData.totals?.deliveryCommission)}</span>
                    </div>
                    <div className="summary-item">
                      <label>Total Surge Revenue</label>
                      <span>{formatCurrency(reportData.totals?.surgeRevenue)}</span>
                    </div>
                    <div className="summary-item">
                      <label>Total Addon Revenue</label>
                      <span>{formatCurrency(reportData.totals?.addonRevenue)}</span>
                    </div>
                    <div className="summary-item highlight">
                      <label>Gross Platform Revenue</label>
                      <span>{formatCurrency(reportData.totals?.grossRevenue)}</span>
                    </div>
                  </div>
                </div>

                {reportData.breakdown?.length > 0 && (
                  <div className="report-breakdown">
                    <h4>📈 Revenue Breakdown</h4>
                    <table className="report-table">
                      <thead>
                        <tr>
                          <th>Period</th>
                          <th>Platform Fees</th>
                          <th>Delivery Commission</th>
                          <th>Surge</th>
                          <th>Addon</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.breakdown.map((row, index) => (
                          <tr key={index}>
                            <td>{row._id}</td>
                            <td>{formatCurrency(row.platformFees)}</td>
                            <td>{formatCurrency(row.deliveryCommission)}</td>
                            <td>{formatCurrency(row.surgeRevenue)}</td>
                            <td>{formatCurrency(row.addonRevenue)}</td>
                            <td className="total">{formatCurrency(row.totalRevenue)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Commission Report */}
            {activeReport === 'commissions' && (
              <div className="commission-report">
                <div className="report-summary">
                  <h3>📊 Commission Summary</h3>
                  <div className="summary-grid">
                    <div className="summary-item">
                      <label>Platform Commission</label>
                      <span>{formatCurrency(reportData.platformCommission?.total)}</span>
                      <small>{reportData.platformCommission?.count || 0} transactions</small>
                    </div>
                    <div className="summary-item">
                      <label>Delivery Commission</label>
                      <span>{formatCurrency(reportData.deliveryCommission?.total)}</span>
                      <small>{reportData.deliveryCommission?.count || 0} transactions</small>
                    </div>
                    <div className="summary-item highlight">
                      <label>Total Commission</label>
                      <span>{formatCurrency(reportData.totalCommission)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* TDS Report */}
            {activeReport === 'tds' && (
              <div className="tds-report">
                <div className="report-summary">
                  <h3>📑 TDS Summary</h3>
                  <div className="summary-grid">
                    <div className="summary-item">
                      <label>Total TDS Deducted</label>
                      <span>{formatCurrency(reportData.totalTdsDeducted)}</span>
                    </div>
                    <div className="summary-item">
                      <label>Transaction Count</label>
                      <span>{reportData.transactionCount || 0}</span>
                    </div>
                    <div className="summary-item">
                      <label>TDS Rate Applied</label>
                      <span>{reportData.tdsRate || 0.1}%</span>
                    </div>
                  </div>
                </div>

                {reportData.byRestaurant?.length > 0 && (
                  <div className="report-breakdown">
                    <h4>🏪 TDS by Restaurant</h4>
                    <table className="report-table">
                      <thead>
                        <tr>
                          <th>Restaurant</th>
                          <th>TDS Deducted</th>
                          <th>Transactions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.byRestaurant.map((row, index) => (
                          <tr key={index}>
                            <td>{row.restaurantName || row._id}</td>
                            <td>{formatCurrency(row.totalTds)}</td>
                            <td>{row.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* GST Report */}
            {activeReport === 'gst' && (
              <div className="gst-report">
                <div className="report-summary">
                  <h3>📋 GST Summary</h3>
                  <div className="summary-grid">
                    <div className="summary-item">
                      <label>Total GST Collected</label>
                      <span>{formatCurrency(reportData.totalGstCollected)}</span>
                    </div>
                    <div className="summary-item">
                      <label>CGST</label>
                      <span>{formatCurrency(reportData.breakdown?.cgst)}</span>
                    </div>
                    <div className="summary-item">
                      <label>SGST</label>
                      <span>{formatCurrency(reportData.breakdown?.sgst)}</span>
                    </div>
                    <div className="summary-item">
                      <label>IGST</label>
                      <span>{formatCurrency(reportData.breakdown?.igst)}</span>
                    </div>
                  </div>
                </div>

                {reportData.bySource?.length > 0 && (
                  <div className="report-breakdown">
                    <h4>📊 GST by Source</h4>
                    <table className="report-table">
                      <thead>
                        <tr>
                          <th>Source</th>
                          <th>GST Amount</th>
                          <th>Transactions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.bySource.map((row, index) => (
                          <tr key={index}>
                            <td>{row._id?.replace(/_/g, ' ')}</td>
                            <td>{formatCurrency(row.totalGst)}</td>
                            <td>{row.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Subscription Report */}
            {activeReport === 'subscriptions' && (
              <div className="subscription-report">
                <div className="report-summary">
                  <h3>📅 Subscription Summary</h3>
                  <div className="summary-grid">
                    <div className="summary-item">
                      <label>Total Revenue</label>
                      <span>{formatCurrency(reportData.totalRevenue)}</span>
                    </div>
                    <div className="summary-item">
                      <label>Active Subscriptions</label>
                      <span>{reportData.activeSubscriptions || 0}</span>
                    </div>
                    <div className="summary-item">
                      <label>New This Period</label>
                      <span>{reportData.newSubscriptions || 0}</span>
                    </div>
                    <div className="summary-item">
                      <label>Renewals</label>
                      <span>{reportData.renewals || 0}</span>
                    </div>
                  </div>
                </div>

                {reportData.byPlan?.length > 0 && (
                  <div className="report-breakdown">
                    <h4>📊 Revenue by Plan</h4>
                    <table className="report-table">
                      <thead>
                        <tr>
                          <th>Plan</th>
                          <th>Revenue</th>
                          <th>Count</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.byPlan.map((row, index) => (
                          <tr key={index}>
                            <td>{row._id}</td>
                            <td>{formatCurrency(row.revenue)}</td>
                            <td>{row.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="no-data">No report data available. Adjust filters and generate report.</div>
        )}
      </div>
    </div>
  );

  // Discrepancies Tab
  const renderDiscrepancies = () => (
    <div className="audit-discrepancies">
      <div className="discrepancies-header">
        <h3>⚠️ Financial Discrepancies</h3>
        <button className="btn-secondary" onClick={fetchDiscrepancies}>
          🔄 Refresh
        </button>
      </div>

      <div className="discrepancies-list">
        {discrepancies.map((disc, index) => (
          <div key={index} className="discrepancy-card" style={{
            borderLeftColor: disc.severity === 'HIGH' ? '#ef4444' :
                            disc.severity === 'MEDIUM' ? '#f59e0b' : '#3b82f6'
          }}>
            <div className="disc-card-header">
              <div className="disc-info">
                <span className="disc-category">{disc.category}</span>
                <span className={`disc-severity ${disc.severity?.toLowerCase()}`}>{disc.severity}</span>
                {getStatusBadge(disc.status)}
              </div>
              <span className="disc-amount">{formatCurrency(disc.amount)}</span>
            </div>
            <p className="disc-description">{disc.description}</p>
            <div className="disc-details">
              <span>Expected: {formatCurrency(disc.expectedAmount)}</span>
              <span>Actual: {formatCurrency(disc.actualAmount)}</span>
              <span>Detected: {formatDate(disc.detectedAt)}</span>
            </div>
            {disc.status === 'PENDING' && (
              <button
                className="btn-resolve"
                onClick={() => {
                  setSelectedDiscrepancy({ ...disc, index });
                  setShowResolveModal(true);
                }}
              >
                Resolve Discrepancy
              </button>
            )}
            {disc.status === 'RESOLVED' && (
              <div className="disc-resolved">
                <span>✅ Resolved: {disc.resolution}</span>
                <span>By: {disc.resolvedBy?.name} on {formatDate(disc.resolvedAt)}</span>
              </div>
            )}
          </div>
        ))}

        {discrepancies.length === 0 && !loading && (
          <div className="no-discrepancies">
            <span className="success-icon">✅</span>
            <h4>No Discrepancies Found</h4>
            <p>All financial records are reconciled correctly.</p>
          </div>
        )}
      </div>

      {/* Resolve Modal */}
      {showResolveModal && selectedDiscrepancy && (
        <div className="modal-overlay" onClick={() => setShowResolveModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🔧 Resolve Discrepancy</h3>
              <button className="modal-close" onClick={() => setShowResolveModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="disc-summary">
                <p><strong>Category:</strong> {selectedDiscrepancy.category}</p>
                <p><strong>Amount:</strong> {formatCurrency(selectedDiscrepancy.amount)}</p>
                <p><strong>Description:</strong> {selectedDiscrepancy.description}</p>
              </div>
              <div className="form-group">
                <label>Resolution Notes *</label>
                <textarea
                  value={resolutionText}
                  onChange={(e) => setResolutionText(e.target.value)}
                  placeholder="Enter resolution details..."
                  rows={4}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowResolveModal(false)}>Cancel</button>
              <button
                className="btn-primary"
                onClick={resolveDiscrepancy}
                disabled={!resolutionText || loading}
              >
                {loading ? 'Resolving...' : 'Mark as Resolved'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Adjustment Modal
  const renderAdjustmentModal = () => (
    showAdjustmentModal && (
      <div className="modal-overlay" onClick={() => setShowAdjustmentModal(false)}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>➕ Create Manual Adjustment</h3>
            <button className="modal-close" onClick={() => setShowAdjustmentModal(false)}>×</button>
          </div>
          <div className="modal-body">
            <div className="form-group">
              <label>Amount * (positive for credit, negative for debit)</label>
              <input
                type="number"
                value={adjustmentForm.amount}
                onChange={(e) => setAdjustmentForm({...adjustmentForm, amount: e.target.value})}
                placeholder="e.g., 100 or -50"
              />
            </div>
            <div className="form-group">
              <label>Description *</label>
              <input
                type="text"
                value={adjustmentForm.description}
                onChange={(e) => setAdjustmentForm({...adjustmentForm, description: e.target.value})}
                placeholder="Reason for adjustment"
              />
            </div>
            <div className="form-group">
              <label>Internal Notes</label>
              <textarea
                value={adjustmentForm.internalNotes}
                onChange={(e) => setAdjustmentForm({...adjustmentForm, internalNotes: e.target.value})}
                placeholder="Internal notes (not visible to users)"
                rows={3}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Order ID (optional)</label>
                <input
                  type="text"
                  value={adjustmentForm.orderId}
                  onChange={(e) => setAdjustmentForm({...adjustmentForm, orderId: e.target.value})}
                  placeholder="Related order ID"
                />
              </div>
              <div className="form-group">
                <label>Restaurant ID (optional)</label>
                <input
                  type="text"
                  value={adjustmentForm.restaurantId}
                  onChange={(e) => setAdjustmentForm({...adjustmentForm, restaurantId: e.target.value})}
                  placeholder="Related restaurant ID"
                />
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn-secondary" onClick={() => setShowAdjustmentModal(false)}>Cancel</button>
            <button
              className="btn-primary"
              onClick={createAdjustment}
              disabled={!adjustmentForm.amount || !adjustmentForm.description || loading}
            >
              {loading ? 'Creating...' : 'Create Adjustment'}
            </button>
          </div>
        </div>
      </div>
    )
  );

  // ============================================================
  // MAIN RENDER
  // ============================================================

  return (
    <div className="finance-audit-page">
      <Navbar />
      <div className="container">
        {/* Header */}
        <div className="page-header">
          <h1>💰 Finance Audit Management</h1>
          <p>Comprehensive financial auditing and reconciliation</p>
        </div>

        {/* Message Alert */}
        {message.text && (
          <div className={`alert ${message.type}`}>
            {message.type === 'success' ? '✅' : '❌'} {message.text}
          </div>
        )}

        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab-btn ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            📊 Dashboard
          </button>
          <button
            className={`tab-btn ${activeTab === 'transactions' ? 'active' : ''}`}
            onClick={() => setActiveTab('transactions')}
          >
            📝 Transactions
          </button>
          <button
            className={`tab-btn ${activeTab === 'reconciliation' ? 'active' : ''}`}
            onClick={() => setActiveTab('reconciliation')}
          >
            📑 Reconciliation
          </button>
          <button
            className={`tab-btn ${activeTab === 'reports' ? 'active' : ''}`}
            onClick={() => setActiveTab('reports')}
          >
            📈 Reports
          </button>
          <button
            className={`tab-btn ${activeTab === 'discrepancies' ? 'active' : ''}`}
            onClick={() => setActiveTab('discrepancies')}
          >
            ⚠️ Discrepancies
          </button>
        </div>

        {/* Loading Indicator */}
        {loading && (
          <div className="loading-bar">
            <div className="loading-progress"></div>
          </div>
        )}

        {/* Tab Content */}
        <div className="tab-content">
          {activeTab === 'dashboard' && renderDashboard()}
          {activeTab === 'transactions' && renderTransactions()}
          {activeTab === 'reconciliation' && renderReconciliation()}
          {activeTab === 'reports' && renderReports()}
          {activeTab === 'discrepancies' && renderDiscrepancies()}
        </div>

        {/* Modals */}
        {renderAdjustmentModal()}
      </div>
    </div>
  );
};

export default FinanceAuditManagement;
